import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { getSwipeListings, swipeListing, getSwipeStats, resetSwipeHistory, getSwipePreferences, createCheckoutSession } from '../services/api';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { 
  Heart, X, MapPin, ChevronUp, ChevronLeft, ChevronRight,
  Settings, Check, Home, RotateCcw, Info,
  Maximize2, Crown, Zap, Sofa, Eye, ArrowLeft, Send, MessageSquare, RefreshCw, Loader2,
  Star, ChevronDown, Shield, Clock, Users, Building2
} from 'lucide-react';

const SwipeInterfacePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { language, getTypeLabel } = useLanguage();
  const { toast } = useToast();
  const cardRef = useRef(null);
  
  const [listings, setListings] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [stats, setStats] = useState({ totalSwipes: 0, likes: 0, passes: 0, todaySwipes: 0, freeSwipesRemaining: 999 });
  const [showMatch, setShowMatch] = useState(false);
  const [matchedListing, setMatchedListing] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [swipeHistory, setSwipeHistory] = useState([]);
  const [todaySwipesUsed, setTodaySwipesUsed] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showFeedback, setShowFeedback] = useState(null); // 'liked' | 'passed' | null
  const [resettingHistory, setResettingHistory] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(null); // null = loading, true/false = checked
  const [checkoutLoading, setCheckoutLoading] = useState(false); // For direct Stripe checkout
  const [selectedPlan, setSelectedPlan] = useState('monthly'); // For paywall plan selection
  const [expandedFaq, setExpandedFaq] = useState(null); // For paywall FAQ
  const [navigatingToPremium, setNavigatingToPremium] = useState(false);

  // Handle navigation to premium with loading animation
  const handleNavigateToPremium = async () => {
    setNavigatingToPremium(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    navigate('/premium');
  };

  // FREE FOR ALL - No swipe limits!
  const FREE_SWIPES_LIMIT = 999999;
  
  // ALL USERS HAVE FULL ACCESS - No subscription needed
  const hasSubscription = true;
  
  const canSwipe = true; // Everyone can swipe!
  const freeSwipesRemaining = 999999;

  const handleResetHistory = async () => {
    // Free for everyone - no premium check needed
    setResettingHistory(true);
    try {
      await resetSwipeHistory();
      toast({ 
        title: language === 'nl' ? 'Swipe history gereset!' : 'Swipe history reset!',
        description: language === 'nl' ? 'Je kunt nu opnieuw alle woningen bekijken' : 'You can now view all properties again'
      });
      // Reload listings after reset
      await loadListings();
    } catch (error) {
      toast({ 
        title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong',
        variant: 'destructive'
      });
    } finally {
      setResettingHistory(false);
    }
  };

  // Direct Stripe checkout - no intermediate page for higher conversion
  const handleDirectCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const result = await createCheckoutSession('swipematch_monthly');
      if (result.url) {
        // Redirect directly to Stripe checkout
        window.location.href = result.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong',
        description: language === 'nl' ? 'Probeer het opnieuw' : 'Please try again',
        variant: 'destructive'
      });
      setCheckoutLoading(false);
    }
  };

  const loadListings = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading swipe listings...');
      const [listingsRes, statsRes] = await Promise.all([
        getSwipeListings().catch((err) => {
          console.error('Error fetching listings:', err.response?.status, err.response?.data);
          if (err.response?.status === 400 || err.response?.data?.detail?.includes('voorkeuren')) {
            navigate('/swipematch');
            return { listings: [] };
          }
          return { listings: [] };
        }),
        getSwipeStats().catch(() => ({ totalSwipes: 0, likes: 0, passes: 0, todaySwipes: 0, freeSwipesRemaining: 3 }))
      ]);
      console.log('Listings loaded:', listingsRes.listings?.length || 0);
      setListings(listingsRes.listings || []);
      setStats(statsRes);
      // Use todaySwipes from backend for accurate daily tracking
      setTodaySwipesUsed(statsRes.todaySwipes || 0);
      setCurrentIndex(0);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Check if user has preferences set
    const checkPreferences = async () => {
      try {
        const res = await getSwipePreferences();
        // Allow swiping if user has ANY saved preferences (cities OR preferences exist)
        if (res.preferences && (res.preferences.cities?.length > 0 || res.preferences.maxPrice || res.preferences.propertyTypes?.length > 0)) {
          setHasPreferences(true);
          loadListings();
        } else {
          // No preferences at all, redirect to onboarding
          setHasPreferences(false);
          navigate('/swipematch');
        }
      } catch (error) {
        console.error('Error checking preferences:', error);
        // On error, still try to load listings (be permissive)
        setHasPreferences(true);
        loadListings();
      }
    };
    
    checkPreferences();
  }, [isAuthenticated, authLoading, navigate]);

  const currentListing = listings[currentIndex];

  const handleSwipe = async (direction) => {
    if (swiping || !currentListing) return;
    
    if (!hasSubscription && todaySwipesUsed >= FREE_SWIPES_LIMIT) {
      setShowPaywall(true);
      return;
    }
    
    setSwiping(true);
    setSwipeDirection(direction);
    setSwipeHistory(prev => [...prev, { index: currentIndex, listing: currentListing }]);

    try {
      const result = await swipeListing(currentListing.id, direction);
      
      const newTodaySwipeCount = todaySwipesUsed + 1;
      setTodaySwipesUsed(newTodaySwipeCount);
      
      setStats(prev => ({
        ...prev,
        totalSwipes: prev.totalSwipes + 1,
        todaySwipes: newTodaySwipeCount,
        likes: direction === 'right' ? prev.likes + 1 : prev.likes,
        passes: direction === 'left' ? prev.passes + 1 : prev.passes
      }));

      // Show feedback popup
      if (direction === 'right') {
        // Like - show message sent feedback
        setShowFeedback('liked');
        setTimeout(() => setShowFeedback(null), 2000);
      } else {
        // Pass - show "niks voor mij" feedback
        setShowFeedback('passed');
        setTimeout(() => setShowFeedback(null), 1500);
      }

      if (!hasSubscription && newTodaySwipeCount >= FREE_SWIPES_LIMIT) {
        setTimeout(() => setShowPaywall(true), 2500);
      }

      setTimeout(() => {
        setSwipeDirection(null);
        setSwiping(false);
        setCurrentIndex(prev => prev + 1);
        setCurrentImageIndex(0);
        setDragOffset({ x: 0, y: 0 });
        setShowDetail(false);
      }, 400);
    } catch (error) {
      console.error('Error swiping:', error);
      if (error.response?.status === 402 || error.response?.status === 403) {
        setShowPaywall(true);
      } else {
        toast({ title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
      }
      setSwiping(false);
      setSwipeDirection(null);
    }
  };

  const handleUndo = () => {
    if (swipeHistory.length === 0) return;
    const last = swipeHistory[swipeHistory.length - 1];
    setSwipeHistory(prev => prev.slice(0, -1));
    setCurrentIndex(last.index);
    setShowDetail(false);
    toast({ title: 'Laatste swipe ongedaan gemaakt' });
  };

  const handleKeyDown = useCallback((e) => {
    if (showMatch || showPaywall || showDetail) return;
    if (e.key === 'ArrowLeft') handleSwipe('left');
    if (e.key === 'ArrowRight') handleSwipe('right');
  }, [currentListing, swiping, showMatch, showPaywall, showDetail]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleDragStart = (e) => {
    if (showPaywall || showDetail) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX, y: clientY });
  };

  const handleDragMove = (e) => {
    if (!dragStart || showPaywall || showDetail) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragOffset({ x: clientX - dragStart.x, y: clientY - dragStart.y });
  };

  const handleDragEnd = () => {
    if (!dragStart) return;
    if (Math.abs(dragOffset.x) > 100) {
      handleSwipe(dragOffset.x > 0 ? 'right' : 'left');
    }
    setDragStart(null);
    if (Math.abs(dragOffset.x) <= 100) {
      setDragOffset({ x: 0, y: 0 });
    }
  };

  const nextImage = (e) => {
    e?.stopPropagation();
    if (!currentListing) return;
    const images = currentListing.images || [];
    if (images.length > 1) {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    if (!currentListing) return;
    const images = currentListing.images || [];
    if (images.length > 1) {
      setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800';
    return image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${image}` : image;
  };

  const rotation = dragOffset.x * 0.02;
  const opacity = 1 - Math.abs(dragOffset.x) / 800;

  // Loading overlay for premium navigation
  if (navigatingToPremium) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] flex items-center justify-center z-50">
        <div className="text-center">
          <div className="mb-6 animate-pulse">
            <SwipeMatchLogo size="large" variant="white" className="justify-center" />
          </div>
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
            </div>
            <p className="text-white/80 text-sm font-medium">
              {language === 'nl' ? 'Premium opties laden...' : 'Loading premium options...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#F7F7F7] flex items-center justify-center" data-testid="swipe-loading">
        <div className="text-center">
          <div className="w-20 h-20 bg-[#2E7D32] rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse shadow-lg">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <p className="text-[#9E9E9E] text-lg font-medium">{language === 'nl' ? 'Woningen laden...' : 'Loading properties...'}</p>
        </div>
      </div>
    );
  }

  // Paywall - Extended version with multiple plans, testimonials and FAQ
  if (showPaywall) {
    const plans = [
      {
        id: 'weekly',
        duration: language === 'nl' ? '1 week' : '1 week',
        price: '€4,99',
        pricePerWeek: '€4,99/week',
        popular: false,
        discount: null,
        description: language === 'nl' ? 'Perfect om snel te reageren' : 'Perfect for quick responses'
      },
      {
        id: 'monthly',
        duration: language === 'nl' ? '4 weken' : '4 weeks',
        price: '€14,99',
        pricePerWeek: '€3,75/week',
        popular: true,
        discount: '25%',
        description: language === 'nl' ? 'Meest gekozen door actieve zoekers' : 'Most chosen by active seekers',
        popularText: language === 'nl' ? '60% kiest dit abonnement.' : '60% choose this subscription.'
      },
      {
        id: 'quarterly',
        duration: language === 'nl' ? '12 weken' : '12 weeks',
        price: '€29,99',
        pricePerWeek: '€2,50/week',
        popular: false,
        discount: '50%',
        description: language === 'nl' ? 'Beste waarde voor lange zoektocht' : 'Best value for long search'
      }
    ];

    const features = [
      { icon: MessageSquare, text: language === 'nl' ? 'Unlimited berichten' : 'Unlimited messages' },
      { icon: Users, text: language === 'nl' ? 'Direct contact met geverifieerde verhuurders' : 'Direct contact with verified landlords' },
      { icon: Building2, text: language === 'nl' ? 'Toegang tot alle woningen' : 'Access to all properties' },
      { icon: Zap, text: language === 'nl' ? 'Snelle support' : 'Fast support' },
      { icon: Clock, text: language === 'nl' ? 'Op elk moment opzegbaar' : 'Cancel anytime' }
    ];

    const testimonials = [
      { name: 'Suruthi', date: '2 Feb 2024', rating: 5, text: language === 'nl' ? 'Ik vind Swipe je woning een betrouwbare website, heb het 2 keer eerder gebruikt en beide keren success.' : 'I find Swipe je woning a reliable website.', color: 'bg-pink-500' },
      { name: 'Lisanne', date: '19 Aug 2023', rating: 5, text: language === 'nl' ? 'Geweldig aanbod! Meeste aanbieders reageren redelijk snel. Heb binnen 2 weken een kamer gevonden.' : 'Great selection! Found a room within 2 weeks.', color: 'bg-purple-500' },
      { name: 'Ali', date: '17 Okt 2022', rating: 5, text: language === 'nl' ? 'Heel betrouwbare manier om aan een kamer te komen. Je hebt gemakkelijk contact met huisbazen.' : 'Very reliable way to find a room.', color: 'bg-blue-500' },
      { name: 'Jax', date: '24 Jan 2024', rating: 5, text: language === 'nl' ? 'Swipe je woning biedt kansen om een kamer te vinden in een vreemde stad waar niemand kent.' : 'Great for finding rooms in new cities.', color: 'bg-green-500' },
      { name: 'Tim', date: '12 Maart 2024', rating: 5, text: language === 'nl' ? 'Snelle reacties en heel veel kamers en studio\'s. Ben tot nu toe geen scammers tegengekomen.' : 'Quick responses and lots of options.', color: 'bg-orange-500' },
      { name: 'Maria', date: '30 Aug 2023', rating: 5, text: language === 'nl' ? 'Ik heb een kamer kunnen vinden vanwege het design. Je kan heel veel informatie vinden over de kamer.' : 'Easy to find detailed info about rooms.', color: 'bg-teal-500' }
    ];

    const faqs = [
      { question: language === 'nl' ? 'Wat is een Premium Account?' : 'What is a Premium Account?', answer: language === 'nl' ? 'Met een Premium Account kun je onbeperkt berichten sturen naar verhuurders en heb je toegang tot alle woningen.' : 'With Premium you can send unlimited messages to landlords and access all properties.' },
      { question: language === 'nl' ? 'Hoe verloopt de betaling?' : 'How does payment work?', answer: language === 'nl' ? 'Je betaalt veilig via Stripe met iDEAL, creditcard of andere betaalmethoden.' : 'You pay securely via Stripe with iDEAL, credit card or other methods.' },
      { question: language === 'nl' ? 'Hoe zeg ik het abonnement op?' : 'How do I cancel?', answer: language === 'nl' ? 'Je kunt op elk moment opzeggen in je profielinstellingen.' : 'You can cancel anytime in your profile settings.' },
      { question: language === 'nl' ? 'Wanneer wordt het abonnement verlengd?' : 'When is it renewed?', answer: language === 'nl' ? 'Je abonnement wordt automatisch verlengd aan het einde van de periode, tenzij je opzegt.' : 'Auto-renews at period end unless cancelled.' }
    ];

    const handlePlanCheckout = async (planId) => {
      const packageMap = { 'weekly': 'swipematch_weekly', 'monthly': 'swipematch_monthly', 'quarterly': 'swipematch_quarterly' };
      setCheckoutLoading(true);
      try {
        const result = await createCheckoutSession(packageMap[planId], '/swipematch/swipe');
        if (result.url) window.location.href = result.url;
      } catch (error) {
        toast({ title: language === 'nl' ? 'Fout' : 'Error', description: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
        setCheckoutLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-[#0D2137] overflow-y-auto" data-testid="swipe-paywall">
        {/* Header */}
        <div className="px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <SwipeMatchLogo size="default" variant="white" />
          <div className="w-10" />
        </div>

        {/* Hero Section */}
        <div className="px-4 pb-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#C04A4A] text-white px-3 py-1.5 rounded-full text-xs font-bold mb-4 animate-pulse">
            <Zap className="w-3 h-3" />
            {language === 'nl' ? 'JE BENT ER BIJNA' : 'YOU\'RE ALMOST THERE'}
          </div>
          <h1 className="text-2xl md:text-4xl font-black text-white mb-3 leading-tight">
            {language === 'nl' ? 'Nog 15+ woningen' : '15+ more properties'}<br/>{language === 'nl' ? 'wachten op jou' : 'waiting for you'}
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-md mx-auto">
            {stats.likes > 0 && (language === 'nl' ? `Je hebt al ${stats.likes} ${stats.likes === 1 ? 'match' : 'matches'}! ` : `You have ${stats.likes} match${stats.likes === 1 ? '' : 'es'}! `)}
            {language === 'nl' ? 'Mis niet de kans op je droomwoning.' : 'Don\'t miss your dream home.'}
          </p>

          {/* Stats */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mt-6 max-w-sm mx-auto">
            <div className="grid grid-cols-3 gap-2 text-center text-white">
              <div><div className="text-xl font-black">{stats.totalSwipes}</div><div className="text-[10px] opacity-70">{language === 'nl' ? 'Bekeken' : 'Viewed'}</div></div>
              <div><div className="text-xl font-black text-[#2E7D32]">{stats.likes}</div><div className="text-[10px] opacity-70">{language === 'nl' ? 'Geliked' : 'Liked'}</div></div>
              <div><div className="text-xl font-black">0</div><div className="text-[10px] opacity-70">{language === 'nl' ? 'Nog over' : 'Remaining'}</div></div>
            </div>
          </div>
        </div>

        {/* Main Content - White Background */}
        <div className="bg-[#F5F7FA] rounded-t-3xl min-h-screen">
          <div className="max-w-6xl mx-auto px-4 py-8">
            
            {/* Subscription Section */}
            <h2 className="text-xl md:text-2xl font-black text-[#1F3A5F] mb-6">
              {language === 'nl' ? 'Kies je abonnement' : 'Choose your subscription'}
            </h2>

            <div className="grid lg:grid-cols-2 gap-6 mb-12">
              {/* Left: Plan Options */}
              <div className="space-y-3">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative bg-white rounded-xl p-4 cursor-pointer transition-all shadow-sm hover:shadow-md ${selectedPlan === plan.id ? 'ring-2 ring-[#2E7D32]' : 'border border-[#E5E5E5]'}`}
                  >
                    {plan.popular && <div className="absolute -top-2.5 left-4 bg-[#2E7D32] text-white text-xs font-bold px-3 py-1 rounded-full">{language === 'nl' ? 'Meest populair' : 'Most popular'}</div>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === plan.id ? 'border-[#2E7D32] bg-[#2E7D32]' : 'border-[#9E9E9E]'}`}>
                          {selectedPlan === plan.id && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-black text-[#1F3A5F]">{plan.price}</span>
                          </div>
                          <p className="text-sm font-medium text-[#1F3A5F]">{plan.duration}</p>
                          <p className="text-xs text-[#9E9E9E]">{plan.description}</p>
                          {plan.popularText && <p className="text-xs text-[#2E7D32] font-medium">{plan.popularText}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {plan.discount && <div className="bg-[#C04A4A] text-white text-xs font-bold px-2 py-1 rounded-full">BESPAAR {plan.discount}</div>}
                        <Button onClick={(e) => { e.stopPropagation(); handlePlanCheckout(plan.id); }} disabled={checkoutLoading} className={`px-4 h-9 rounded-full font-bold text-sm ${selectedPlan === plan.id ? 'bg-[#1F3A5F] text-white' : 'bg-white border-2 border-[#1F3A5F] text-[#1F3A5F]'}`}>
                          {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'nl' ? 'Selecteer' : 'Select')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right: Features */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#E5E5E5] h-fit">
                <h3 className="text-lg font-black text-[#1F3A5F] mb-4">{language === 'nl' ? 'Elk abonnement komt met' : 'Every subscription includes'}</h3>
                <div className="space-y-3">
                  {features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-7 h-7 bg-[#2E7D32]/10 rounded-full flex items-center justify-center"><Check className="w-3.5 h-3.5 text-[#2E7D32]" /></div>
                      <span className="text-sm text-[#2A2A2A]">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-12">
              <h2 className="text-xl md:text-2xl font-black text-[#1F3A5F] mb-2 text-center">
                {language === 'nl' ? 'Vorig jaar vonden 30.000+ huurders een thuis' : 'Last year 30,000+ renters found a home'}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {testimonials.map((t, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-[#E5E5E5]">
                    <div className="flex items-center justify-between mb-2"><span className="text-xs text-[#9E9E9E]">{t.date}</span></div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 ${t.color} rounded-full flex items-center justify-center text-white font-bold text-sm`}>{t.name.charAt(0)}</div>
                      <div className="flex gap-0.5">{[...Array(t.rating)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 text-[#00B67A] fill-[#00B67A]" />)}</div>
                    </div>
                    <p className="font-bold text-[#2A2A2A] text-sm mb-1">{t.name}</p>
                    <p className="text-xs text-[#6B6B6B] leading-relaxed">{t.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="max-w-2xl mx-auto mb-8">
              <h2 className="text-xl md:text-2xl font-black text-[#1F3A5F] mb-6">{language === 'nl' ? 'Veelgestelde vragen' : 'FAQ'}</h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
                    <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-4 py-3 flex items-center justify-between text-left">
                      <span className="font-bold text-[#2A2A2A] text-sm">{faq.question}</span>
                      {expandedFaq === i ? <ChevronUp className="w-4 h-4 text-[#9E9E9E]" /> : <ChevronDown className="w-4 h-4 text-[#9E9E9E]" />}
                    </button>
                    {expandedFaq === i && <div className="px-4 pb-3 text-sm text-[#6B6B6B]">{faq.answer}</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Skip */}
            <button onClick={() => navigate('/')} className="w-full text-center text-[#9E9E9E] text-sm py-4 hover:text-[#2A2A2A]">
              {language === 'nl' ? 'Nee bedankt' : 'No thanks'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No more listings
  if (!currentListing || currentIndex >= listings.length) {
    // If listings is empty, it might be a loading/API issue - show refresh option
    const hasNoListingsLoaded = listings.length === 0;
    
    return (
      <div className="fixed inset-0 bg-[#F7F7F7] flex flex-col" data-testid="swipe-complete">
        <div className="bg-[#F7F7F7] px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-[#E5E5E5] shadow-sm">
            <Home className="w-5 h-5 text-[#2A2A2A]" />
          </button>
          <SwipeMatchLogo size="small" variant="color" />
          <button onClick={() => navigate('/dashboard')} className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-[#E5E5E5] shadow-sm">
            <Heart className="w-5 h-5 text-[#2E7D32]" />
          </button>
        </div>

        <div className="flex-1 flex items-start sm:items-center justify-center p-4 pt-6 overflow-y-auto">
          <div className="max-w-md w-full text-center">
            {/* Checkmark with animation */}
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 mx-auto mb-6 mt-4">
              <div className="absolute inset-[-8px] bg-[#2E7D32]/15 rounded-full animate-ping" />
              <div className="relative w-24 h-24 sm:w-28 sm:h-28 bg-[#2E7D32] rounded-full flex items-center justify-center shadow-xl">
                <Check className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
              </div>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-[#2A2A2A] mb-2">
              {language === 'nl' ? 'Alles bekeken!' : 'All viewed!'}
            </h2>
            <p className="text-[#9E9E9E] mb-5 text-sm sm:text-base px-4">
              {language === 'nl' ? 'Er komen dagelijks nieuwe woningen bij!' : 'New properties are added daily!'}
            </p>
            
            {/* Show reload button if no listings were loaded at all */}
            {hasNoListingsLoaded && (
              <Button 
                onClick={() => loadListings()} 
                className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-12 rounded-xl font-semibold mb-4 w-full max-w-xs mx-auto"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {language === 'nl' ? 'Opnieuw laden' : 'Reload'}
              </Button>
            )}
            {/* Session Stats Card */}
            <div className="bg-white rounded-2xl p-4 sm:p-5 mb-5 border border-[#E5E5E5] shadow-sm mx-auto">
              <p className="text-xs text-[#9E9E9E] uppercase tracking-wide mb-3">
                {language === 'nl' ? 'Jouw sessie' : 'Your session'}
              </p>
              <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#2A2A2A]">{todaySwipesUsed}</div>
                  <div className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Bekeken' : 'Viewed'}</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#2E7D32]">{stats.likes}</div>
                  <div className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Geliked' : 'Liked'}</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#C04A4A]">{stats.passes}</div>
                  <div className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Geskipt' : 'Skipped'}</div>
                </div>
              </div>
            </div>

            {/* Free swipes info - removed since app is 100% free */}
            
            <div className="flex flex-col gap-3">
              {stats.likes > 0 && (
                <Button onClick={() => navigate('/dashboard')} className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-12 sm:h-14 rounded-xl font-semibold shadow-sm text-sm sm:text-base">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5 mr-2 fill-white" />
                  {language === 'nl' ? `Bekijk je ${stats.likes} match${stats.likes !== 1 ? 'es' : ''}` : `View your ${stats.likes} match${stats.likes !== 1 ? 'es' : ''}`}
                </Button>
              )}
              
              <Button onClick={() => navigate('/profiel')} variant={stats.likes > 0 ? "outline" : "default"} className={stats.likes > 0 ? "border-[#E5E5E5] text-[#2A2A2A] hover:bg-white h-11 sm:h-12 rounded-xl text-sm" : "bg-[#1F3A5F] hover:bg-[#162d4d] text-white h-12 sm:h-14 rounded-xl font-semibold shadow-sm text-sm sm:text-base"}>
                <Settings className="w-4 h-4 mr-2" />
                {language === 'nl' ? 'Voorkeuren aanpassen' : 'Adjust preferences'}
              </Button>
            </div>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#E5E5E5]" />
              <span className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'OF' : 'OR'}</span>
              <div className="flex-1 h-px bg-[#E5E5E5]" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => navigate('/dashboard')} className="bg-white border border-[#E5E5E5] rounded-xl p-3 sm:p-4 text-center hover:border-[#2E7D32] transition-colors">
                <Home className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-[#2A2A2A]" />
                <span className="text-xs sm:text-sm font-medium text-[#2A2A2A]">Dashboard</span>
              </button>
              {hasSubscription ? (
                <button 
                  onClick={handleResetHistory} 
                  disabled={resettingHistory}
                  className="bg-[#2E7D32] border border-[#2E7D32] rounded-xl p-3 sm:p-4 text-center hover:bg-[#256B2B] transition-colors"
                >
                  {resettingHistory ? (
                    <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-white animate-spin" />
                  ) : (
                    <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-white" />
                  )}
                  <span className="text-xs sm:text-sm font-medium text-white">
                    {language === 'nl' ? 'Opnieuw swipen' : 'Swipe again'}
                  </span>
                </button>
              ) : (
                <button 
                  onClick={handleNavigateToPremium}
                  disabled={navigatingToPremium}
                  className="bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] border border-transparent rounded-xl p-3 sm:p-4 text-center hover:opacity-90 transition-opacity"
                >
                  <Crown className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2 text-white" />
                  <span className="text-xs sm:text-sm font-medium text-white">
                    {language === 'nl' ? 'Ga Premium' : 'Go Premium'}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const images = currentListing.images || [];
  const currentImage = images[currentImageIndex] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800';

  // Detail View - Full screen info panel
  if (showDetail) {
    return (
      <div className="fixed inset-0 bg-white z-50 overflow-y-auto" data-testid="listing-detail">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-[#E5E5E5] px-4 py-3 z-10">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <button onClick={() => setShowDetail(false)} className="p-2 hover:bg-[#F7F7F7] rounded-full">
              <ArrowLeft className="w-5 h-5 text-[#2A2A2A]" />
            </button>
            <h1 className="text-lg font-bold text-[#2A2A2A]">
              {language === 'nl' ? 'Woning details' : 'Property details'}
            </h1>
            <div className="w-9" />
          </div>
        </div>

        {/* Image Gallery - Improved */}
        <div className="max-w-lg mx-auto">
          <div className="relative aspect-[4/3] bg-[#F7F7F7]">
            <img 
              src={getImageUrl(currentImage)} 
              alt={currentListing.title} 
              className="w-full h-full object-contain"
              style={{ imageRendering: 'auto' }}
            />
            
            {images.length > 1 && (
              <>
                <div className="absolute top-3 left-3 right-3 flex gap-1">
                  {images.map((_, i) => (
                    <div key={i} className={`flex-1 h-1 rounded-full ${i === currentImageIndex ? 'bg-[#2E7D32]' : 'bg-black/20'}`} />
                  ))}
                </div>
                
                <button onClick={prevImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/95 rounded-full flex items-center justify-center shadow-lg border border-[#E5E5E5]">
                  <ChevronLeft className="w-6 h-6 text-[#2A2A2A]" />
                </button>
                <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/95 rounded-full flex items-center justify-center shadow-lg border border-[#E5E5E5]">
                  <ChevronRight className="w-6 h-6 text-[#2A2A2A]" />
                </button>
                
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-3 py-1 rounded-full">
                  {currentImageIndex + 1}/{images.length}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-lg mx-auto p-4 pb-32">
          {/* Price & Title */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <Badge className="bg-[#2E7D32] text-white border-0 text-xl font-bold px-4 py-1.5">
                €{currentListing.price}<span className="text-sm font-normal opacity-80">/{language === 'nl' ? 'mnd' : 'mo'} {currentListing.utilitiesIncluded === true ? (language === 'nl' ? 'incl.' : 'incl.') : currentListing.utilitiesIncluded === false ? (language === 'nl' ? 'excl.' : 'excl.') : ''}</span>
              </Badge>
              {currentListing.furnished && (
                <Badge className="bg-[#E5E5E5] text-[#2A2A2A] border-0">
                  <Sofa className="w-3 h-3 mr-1" />
                  {language === 'nl' ? 'Gemeubileerd' : 'Furnished'}
                </Badge>
              )}
            </div>
            <h2 className="text-2xl font-bold text-[#2A2A2A] mb-1">{currentListing.title}</h2>
            <div className="flex items-center gap-2 text-[#9E9E9E]">
              <MapPin className="w-4 h-4 text-[#2E7D32]" />
              <span>{currentListing.city}{currentListing.neighborhood ? `, ${currentListing.neighborhood}` : ''}</span>
            </div>
          </div>

          {/* Quick Facts */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-[#F7F7F7] rounded-xl p-3 text-center">
              <Home className="w-5 h-5 mx-auto mb-1 text-[#2E7D32]" />
              <p className="text-sm font-medium text-[#2A2A2A] capitalize">{getTypeLabel(currentListing.type)}</p>
              <p className="text-xs text-[#9E9E9E]">Type</p>
            </div>
            <div className="bg-[#F7F7F7] rounded-xl p-3 text-center">
              <Maximize2 className="w-5 h-5 mx-auto mb-1 text-[#2E7D32]" />
              <p className="text-sm font-medium text-[#2A2A2A]">{currentListing.size} m²</p>
              <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Oppervlakte' : 'Size'}</p>
            </div>
            <div className="bg-[#F7F7F7] rounded-xl p-3 text-center">
              <Zap className="w-5 h-5 mx-auto mb-1 text-[#2E7D32]" />
              <p className="text-sm font-medium text-[#2A2A2A]">{currentListing.available || 'Direct'}</p>
              <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Beschikbaar' : 'Available'}</p>
            </div>
          </div>

          {/* Description */}
          {currentListing.description && (
            <div className="mb-6">
              <h3 className="font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Beschrijving' : 'Description'}</h3>
              <p className="text-[#9E9E9E] leading-relaxed">{currentListing.description}</p>
            </div>
          )}

          {/* Features */}
          {currentListing.features?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Kenmerken' : 'Features'}</h3>
              <div className="flex flex-wrap gap-2">
                {currentListing.features.map((feature, i) => (
                  <Badge key={i} variant="secondary" className="bg-[#F7F7F7] text-[#2A2A2A] border-0">
                    {feature}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Landlord Info */}
          {currentListing.landlord && (
            <div className="bg-[#F7F7F7] rounded-xl p-4">
              <h3 className="font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Verhuurder' : 'Landlord'}</h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {currentListing.landlord.name?.[0] || 'V'}
                </div>
                <div>
                  <p className="font-medium text-[#2A2A2A]">{currentListing.landlord.name}</p>
                  {currentListing.landlord.verified && (
                    <p className="text-xs text-[#2E7D32] flex items-center gap-1">
                      <Check className="w-3 h-3" /> {language === 'nl' ? 'Geverifieerd' : 'Verified'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Bottom Actions */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E5] p-4 safe-area-bottom">
          <div className="max-w-lg mx-auto flex gap-3">
            <button
              onClick={() => { setShowDetail(false); handleSwipe('left'); }}
              disabled={swiping}
              className="flex-1 h-14 bg-white border-2 border-[#C04A4A] rounded-xl flex items-center justify-center text-[#C04A4A] font-bold hover:bg-[#C04A4A]/5"
            >
              <X className="w-6 h-6 mr-2" />
              {language === 'nl' ? 'Nee' : 'No'}
            </button>
            <button
              onClick={() => { setShowDetail(false); handleSwipe('right'); }}
              disabled={swiping}
              className="flex-1 h-14 bg-[#2E7D32] rounded-xl flex items-center justify-center text-white font-bold hover:bg-[#256B2B]"
            >
              <Heart className="w-6 h-6 mr-2" />
              Like
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F7F7F7] overflow-hidden select-none" data-testid="swipe-fullscreen">
      <Toaster />

      {/* Feedback Toast - Like (Message Sent) - Subtle top notification */}
      {showFeedback === 'liked' && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-slideUp" data-testid="feedback-liked">
          <div className="bg-[#2E7D32] text-white rounded-full px-5 py-3 shadow-lg flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Send className="w-4 h-4" />
            </div>
            <div>
              <span className="font-bold text-sm">
                {language === 'nl' ? 'Bericht verstuurd!' : 'Message sent!'}
              </span>
              <span className="text-white/80 text-xs ml-2">
                {language === 'nl' ? 'Verhuurder ontvangt je intro' : 'Landlord receives your intro'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Toast - Pass (Niks voor mij) - Subtle top notification */}
      {showFeedback === 'passed' && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-slideUp" data-testid="feedback-passed">
          <div className="bg-[#9E9E9E] text-white rounded-full px-5 py-2.5 shadow-lg flex items-center gap-2">
            <X className="w-4 h-4" />
            <span className="font-medium text-sm">
              {language === 'nl' ? 'Niks voor mij' : 'Not for me'}
            </span>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-30 p-3 bg-[#F7F7F7]/80 backdrop-blur-sm">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button onClick={() => navigate('/dashboard')} className="w-9 h-9 bg-white rounded-full flex items-center justify-center border border-[#E5E5E5] shadow-sm" data-testid="swipe-back-btn">
            <ChevronLeft className="w-5 h-5 text-[#2A2A2A]" />
          </button>

          <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border border-[#E5E5E5] shadow-sm">
            <Heart className="w-4 h-4 text-[#2E7D32] fill-[#2E7D32]" />
            <span className="font-bold text-[#2A2A2A] text-sm">{stats.likes}</span>
            <div className="w-px h-4 bg-[#E5E5E5]" />
            <span className="text-[#9E9E9E] text-xs">{listings.length - currentIndex} {language === 'nl' ? 'over' : 'left'}</span>
            {!hasSubscription && (
              <>
                <div className="w-px h-4 bg-[#E5E5E5]" />
                <span className={`text-xs font-medium ${freeSwipesRemaining === 0 ? 'text-[#C04A4A]' : 'text-[#2E7D32]'}`}>
                  {freeSwipesRemaining} {language === 'nl' ? 'gratis vandaag' : 'free today'}
                </span>
              </>
            )}
          </div>

          <button onClick={() => navigate('/dashboard')} className="w-9 h-9 bg-white rounded-full flex items-center justify-center border border-[#E5E5E5] shadow-sm">
            <Home className="w-4 h-4 text-[#2A2A2A]" />
          </button>
        </div>
      </div>

      {/* Main Swipe Card */}
      <div className="absolute inset-0 flex items-center justify-center pt-20 pb-28 px-4">
        <div className="relative w-full h-full max-w-[420px] max-h-[600px]">
          <div
            ref={cardRef}
            className={`absolute inset-0 rounded-2xl overflow-hidden bg-white border border-[#E5E5E5] shadow-lg ${swiping ? 'transition-all duration-400' : 'transition-transform duration-100'}`}
            style={{
              transform: swipeDirection === 'left' ? 'translateX(-120%) rotate(-20deg)' : swipeDirection === 'right' ? 'translateX(120%) rotate(20deg)' : `translateX(${dragOffset.x}px) rotate(${rotation}deg)`,
              opacity: swipeDirection ? 0 : opacity
            }}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            {/* Image - 70% */}
            <div className="absolute inset-0 h-[70%]">
              <img src={getImageUrl(currentImage)} alt={currentListing.title} className="w-full h-full object-cover" draggable={false} />
              
              {/* Image Progress */}
              {images.length > 1 && (
                <div className="absolute top-3 left-3 right-3 flex gap-1 z-10">
                  {images.map((_, i) => (
                    <div key={i} className={`flex-1 h-1 rounded-full transition-all ${i === currentImageIndex ? 'bg-white' : 'bg-white/40'}`} />
                  ))}
                </div>
              )}

              {/* Photo Nav Arrows */}
              {images.length > 1 && (
                <>
                  {currentImageIndex > 0 && (
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center z-10 pointer-events-none">
                      <ChevronLeft className="w-5 h-5 text-white" />
                    </div>
                  )}
                  {currentImageIndex < images.length - 1 && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center z-10 pointer-events-none">
                      <ChevronRight className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full z-10">
                    {currentImageIndex + 1}/{images.length} {language === 'nl' ? 'foto\'s' : 'photos'}
                  </div>
                </>
              )}

              {/* Tap zones */}
              {images.length > 1 && (
                <>
                  <div className="absolute left-0 top-0 bottom-0 w-1/4 z-10" onClick={prevImage} />
                  <div className="absolute right-0 top-0 bottom-0 w-1/4 z-10" onClick={nextImage} />
                </>
              )}

              {/* Swipe Labels */}
              <div className="absolute top-20 left-6 transition-all pointer-events-none" style={{ opacity: dragOffset.x < -30 ? Math.min(1, Math.abs(dragOffset.x) / 100) : 0, transform: `scale(${dragOffset.x < -30 ? Math.min(1.1, 0.6 + Math.abs(dragOffset.x) / 200) : 0.6})` }}>
                <div className="bg-[#C04A4A] text-white font-bold text-xl px-5 py-2 rounded-lg rotate-[-15deg] border-4 border-white shadow-xl">NOPE</div>
              </div>
              <div className="absolute top-20 right-6 transition-all pointer-events-none" style={{ opacity: dragOffset.x > 30 ? Math.min(1, dragOffset.x / 100) : 0, transform: `scale(${dragOffset.x > 30 ? Math.min(1.1, 0.6 + dragOffset.x / 200) : 0.6})` }}>
                <div className="bg-[#2E7D32] text-white font-bold text-xl px-5 py-2 rounded-lg rotate-[15deg] border-4 border-white shadow-xl">LIKE</div>
              </div>
            </div>
            
            {/* Info Section - 30% */}
            <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-white p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <Badge className="bg-[#2E7D32] text-white border-0 text-lg font-bold px-3 py-1">
                  €{currentListing.price}<span className="text-sm font-normal opacity-80">/{language === 'nl' ? 'mnd' : 'mo'} {currentListing.utilitiesIncluded === true ? (language === 'nl' ? 'incl.' : 'incl.') : currentListing.utilitiesIncluded === false ? (language === 'nl' ? 'excl.' : 'excl.') : ''}</span>
                </Badge>
                {currentListing.furnished && (
                  <Badge className="bg-[#E5E5E5] text-[#2A2A2A] border-0 text-xs">
                    <Sofa className="w-3 h-3 mr-1" />
                    {language === 'nl' ? 'Gemeubileerd' : 'Furnished'}
                  </Badge>
                )}
              </div>

              <h2 className="text-lg font-bold text-[#2A2A2A] mb-1 leading-tight line-clamp-1">{currentListing.title}</h2>
              
              <div className="flex items-center gap-2 text-[#9E9E9E] text-sm mb-2">
                <MapPin className="w-4 h-4 text-[#2E7D32] flex-shrink-0" />
                <span className="truncate">{currentListing.city}{currentListing.neighborhood ? `, ${currentListing.neighborhood}` : ''}</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="bg-[#F7F7F7] text-[#2A2A2A] border-0 text-xs px-2 py-1">
                  <Home className="w-3 h-3 mr-1" />
                  {getTypeLabel(currentListing.type)}
                </Badge>
                <Badge variant="secondary" className="bg-[#F7F7F7] text-[#2A2A2A] border-0 text-xs px-2 py-1">
                  <Maximize2 className="w-3 h-3 mr-1" />
                  {currentListing.size} m²
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Actions - Simplified & Clear */}
      <div className="absolute bottom-0 left-0 right-0 z-30 pb-6 bg-gradient-to-t from-[#F7F7F7] via-[#F7F7F7]/80 to-transparent pt-8 safe-area-bottom">
        <div className="flex items-center justify-center gap-4 max-w-md mx-auto px-4">
          {/* Undo */}
          <div className="flex flex-col items-center">
            <button
              onClick={handleUndo}
              disabled={swipeHistory.length === 0}
              className="w-12 h-12 bg-white rounded-full shadow-md flex items-center justify-center border border-[#E5E5E5] hover:border-[#1F3A5F] transition-all disabled:opacity-30"
              data-testid="swipe-undo-btn"
            >
              <RotateCcw className="w-5 h-5 text-[#1F3A5F]" />
            </button>
            <span className="text-[10px] text-[#9E9E9E] mt-1">{language === 'nl' ? 'Terug' : 'Undo'}</span>
          </div>

          {/* Nope */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => handleSwipe('left')}
              disabled={swiping}
              className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-[#E5E5E5] hover:border-[#C04A4A] hover:bg-[#C04A4A]/5 transition-all disabled:opacity-50"
              data-testid="swipe-left-btn"
            >
              <X className="w-8 h-8 text-[#C04A4A]" />
            </button>
            <span className="text-[10px] text-[#9E9E9E] mt-1">{language === 'nl' ? 'Nee' : 'No'}</span>
          </div>
          
          {/* Info */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowDetail(true)}
              className="w-12 h-12 bg-white rounded-full shadow-md flex items-center justify-center border border-[#E5E5E5] hover:border-[#2E7D32] transition-all"
              data-testid="swipe-info-btn"
            >
              <Eye className="w-5 h-5 text-[#2E7D32]" />
            </button>
            <span className="text-[10px] text-[#9E9E9E] mt-1">Info</span>
          </div>

          {/* Like */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => handleSwipe('right')}
              disabled={swiping}
              className="w-16 h-16 bg-[#2E7D32] rounded-full shadow-lg flex items-center justify-center hover:bg-[#256B2B] transition-all disabled:opacity-50"
              data-testid="swipe-right-btn"
            >
              <Heart className="w-8 h-8 text-white" />
            </button>
            <span className="text-[10px] text-[#9E9E9E] mt-1">Like</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwipeInterfacePage;
